import { Component, ElementRef, NgZone, OnInit } from '@angular/core';
import { EmployeeModuleMenuService } from '@app/modules/employees/services/employee-module-menu.service';
import { AuthService } from '@app/services/auth.service';
import { NotifyService } from '@app/services/notify.service';
import { CircularRelationshipError, DuplicateEmployeeError } from '@employees/errors';
import { Department } from '@models/company/department.model';
import { Employee } from '@models/employee/employee.model';
import * as domtoimage from 'dom-to-image';
import { saveAs } from 'file-saver';

import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { AnalyticService } from '@app/services';
import { convertToNestedReportsArray } from './org-accordion-convert-to-nested-reports-array';

@Component({
    templateUrl: './organization-accordion.template.html',
    styleUrls: ['./organization-accordion.style.scss'],
})
export class OrganizationAccordionView implements OnInit {
    isLoading = true;
    employees: Employee[];
    filters: any = {
        departmentId: 'employees.organizationChart.departments',
    };
    filterValues: any = {
        departmentId: {},
    };
    togglePhotos = true;
    toggleNames = true;
    togglePositions = true;
    toggleDepartments = true;
    activeFilters: any = {};
    /**
     * Limit number of employees loaded - helps for deving
     * 0 is no limit
     */
    private readonly EMPLOYEE_LIMIT: number = 250;

    constructor(
        private element: ElementRef,
        private auth: AuthService,
        private notify: NotifyService,
        private zone: NgZone,
        private router: Router,
        private analyticsService: AnalyticService,
        private route: ActivatedRoute,
        public employeeModuleMenuService: EmployeeModuleMenuService
    ) {}

    ngOnInit(): void {
        Department.param('company', this.auth.company.id)
            .all()
            .then(([departments]) => {
                for (const department of departments) {
                    this.filterValues.departmentId[department.id] = department.name;
                }
            });

        this.loadData();
    }

    goToChartView(): void {
        this.analyticsService.trackEvent(AnalyticEvents.OrgChartEnterPyramidView);
        this.router.navigate(['../chart'], { relativeTo: this.route });
    }

    onFilter(filters: any): void {
        this.analyticsService.trackEvent(AnalyticEvents.OrgChartAppliedFilter);
        this.activeFilters = filters;
        this.loadData();
    }

    expandAll(): void {
        $(this.element.nativeElement).find('.ui.accordion .title, .ui.accordion .content').addClass('active');
    }

    collapseAll(): void {
        $(this.element.nativeElement).find('.ui.accordion .title, .ui.accordion .content').removeClass('active');
    }

    print(): void {
        this.isLoading = true;
        const thisRef = this;
        this.analyticsService.trackEvent(AnalyticEvents.OrgChartDownloadImage);
        setTimeout(() => {
            domtoimage.toBlob(document.querySelector('.accordion')).then(function (blob) {
                saveAs(blob, `org-chart.png`);
                thisRef.isLoading = false;
            });
        });
    }

    private renderAccordion(): void {
        setTimeout(() => {
            this.findAndExpandActiveEmployee();
        });
    }

    /**
     * Expand the org item for the current employee
     *
     * @return {boolean} [description]
     */
    private findAndExpandActiveEmployee(): boolean {
        const activeEmployee = $(this.element.nativeElement).find('.title[data-id="' + this.auth.employee.id + '"]');

        if (activeEmployee) {
            const parents = $(activeEmployee[1]).parents('app-org-accordion-item');

            for (const parent of parents) {
                const index = $(this.element.nativeElement).find('.title').index($(parent).find('.title'));
                $(this.element.nativeElement).find('.ui.accordion').accordion('open', index);
            }

            const topOffset = $(activeEmployee).offset();
            if (topOffset) {
                $('html, body').animate(
                    {
                        scrollTop: topOffset.top,
                    },
                    250
                );
            }

            return true;
        }

        return false;
    }

    private loadData(): void {
        this.employees = null;
        this.isLoading = true;
        let request = Employee.param('company', this.auth.company.id)
            .limit(this.EMPLOYEE_LIMIT) // Load every employee
            .with('account')
            .with('department')
            .with('job')
            .whereIn('status', ['onLeave', 'active'])
            .orderBy('department_id', 'ASC');

        if (this.activeFilters && Object.keys(this.activeFilters).length) {
            for (const field in this.activeFilters) {
                request = request.where(field, this.activeFilters[field]);
            }
        }
        request.get().then(([employees]) => {
            try {
                this.employees = convertToNestedReportsArray(employees);

                if (!this.employees.length) {
                    this.notify.error('No employees found.');
                }
                this.zone.runOutsideAngular(() => {
                    this.renderAccordion();
                });
                this.zone.runOutsideAngular(() => {
                    this.renderDropdowns();
                });

                this.isLoading = false;
            } catch (error) {
                if (error instanceof DuplicateEmployeeError) {
                    this.notify.error(
                        'Duplicate Employees. Could not load chart because the company has duplicate employees'
                    );
                    this.isLoading = false;
                    return;
                }

                if (error instanceof CircularRelationshipError) {
                    this.notify.error(
                        'Circular Relationship. Could not load chart because reporting structure is circular'
                    );
                    this.isLoading = false;
                    return;
                }

                this.notify.error('Invalid Data. Could not load chart because of invalid data');
                this.isLoading = false;
            }
        });
    }

    private renderDropdowns(): void {
        setTimeout(() => {
            $(this.element.nativeElement).find('.ui.dropdown').dropdown();
        });
    }
}
